/**
 * Canonical in-app / prefs / catalog titles for notification event keys.
 * Prefer these over backend/email marketing copy so list + detail stay consistent.
 *
 * `performance.daily.realized_pnl` is kept for display of historical inbox/email rows
 * only — it is no longer preference-togglable (superseded by daily performance summary).
 *
 * Trial billing keys (`billing.trial.*`, `billing.subscription.trial_will_end`) are
 * kept for historical inbox/email rows only — Tessera no longer offers trials.
 *
 * `automated_strategy.trade_*`, `trade.closed`, and `stats.milestone.profit_streak`
 * are not productized — labels only for any legacy inbox rows.
 */
export const NOTIFICATION_EVENT_LABELS = {
  "performance.daily.realized_pnl": "Daily Realised PnL",
  "performance.daily.new_best_day": "New Best Trading Day",
  "performance.daily.new_worst_day": "New Worst Trading Day",
  "performance.pnl.milestone_crossed": "PnL Milestone Crossed",
  "performance.summary.daily": "Daily Performance Summary",
  "performance.summary.weekly": "Weekly Performance Summary",
  "performance.summary.monthly": "Monthly Performance Summary",
  "system.broker_disconnected": "Broker Disconnected",
  "system.broker_reconnected": "Broker Reconnected",
  "system.sync_failed": "Sync Failed",
  "system.sync_recovered": "Sync Recovered",
  /** @deprecated Trials not offered — historical inbox/email only */
  "billing.trial.started": "Welcome — Trial Is Live",
  /** @deprecated Trials not offered — historical inbox/email only */
  "billing.trial.cancelled": "Trial Cancelled",
  /** @deprecated Trials not offered — historical inbox/email only */
  "billing.subscription.trial_will_end": "Trial Ends Soon",
  /** @deprecated Trials not offered — historical inbox/email only */
  "billing.trial.ended.payment_succeeded": "Subscription Active",
  /** @deprecated Trials not offered — historical inbox/email only */
  "billing.trial.ended.payment_failed": "Payment Failed After Trial",
  "billing.payment_succeeded": "Payment Received",
  "billing.payment_failed": "Payment Failed",
  "billing.invoice.payment_action_required": "Confirm Your Payment",
  "billing.payment_action_required": "Confirm Your Payment",
  "billing.subscription.cancelled": "Subscription Cancelled",
  "billing.subscription.updated": "Subscription Updated",
  "report_ready": "Report Ready",
  "report_failed": "Report Failed",
  "announcement.platform": "Platform Update",
  "trade.closed": "Trade Closed",
  "stats.milestone.profit_streak": "Win Streak Milestone",
  "automated_strategy.trade_executed": "Strategy Trade Executed",
  "automated_strategy.trade_closed": "Strategy Trade Closed",
} as const;

export type NotificationEventLabelKey = keyof typeof NOTIFICATION_EVENT_LABELS;

/** Preference-togglable performance + system keys (Profile → Notifications). */
export const NOTIFICATION_PREFERENCE_EVENT_KEYS = [
  "performance.daily.new_best_day",
  "performance.daily.new_worst_day",
  "performance.pnl.milestone_crossed",
  "performance.summary.daily",
  "performance.summary.weekly",
  "performance.summary.monthly",
  "system.broker_disconnected",
  "system.broker_reconnected",
  "system.sync_failed",
  "system.sync_recovered",
] as const satisfies ReadonlyArray<NotificationEventLabelKey>;

export type NotificationPreferenceEventKey =
  (typeof NOTIFICATION_PREFERENCE_EVENT_KEYS)[number];

export function getNotificationEventLabel(
  eventKey: string | null | undefined
): string | null {
  if (!eventKey) return null;
  const label = NOTIFICATION_EVENT_LABELS[eventKey as NotificationEventLabelKey];
  return label ?? null;
}
